import type {
  AdminStats,
  AuthResponse,
  Company,
  ContactRequest,
  Document,
  DocumentAsset,
  DocumentBlock,
  DocumentExport,
  DocumentLayout,
  DocumentPageSettings,
  DocumentStatus,
  MeResponse,
  PaginatedResponse,
} from "../types/models";
import { debugError, debugLog, debugWarn, isDebug } from "../lib/debug";

const API_URL =
  process.env.NEXT_PUBLIC_API_URL ?? "https://nice.esl.kz/api";

const TOKEN_KEY = "auth_token";

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  errors?: Record<string, string[]>;

  constructor(
    message: string,
    status: number,
    errors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errors = errors;
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  formData?: FormData;
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, formData, headers: customHeaders, ...rest } = options;
  const token = getToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(customHeaders as Record<string, string>),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (body !== undefined && !formData) {
    headers["Content-Type"] = "application/json";
  }

  const startedAt = performance.now();
  const response = await fetch(`${API_URL}${path}`, {
    ...rest,
    credentials: "include",
    headers,
    body: formData ?? (body !== undefined ? JSON.stringify(body) : undefined),
  });

  const durationMs = Math.round(performance.now() - startedAt);

  if (response.status === 204) {
    if (isDebug()) {
      debugLog("api", `${options.method ?? "GET"} ${path} → 204 (${durationMs}ms)`);
    }
    return undefined as T;
  }

  const data = await response.json().catch(() => ({}));

  if (isDebug()) {
    const level = response.ok ? debugLog : debugWarn;
    level("api", `${options.method ?? "GET"} ${path} → ${response.status} (${durationMs}ms)`, {
      errors: data.errors,
      message: data.message,
    });
  }

  if (!response.ok) {
    if (isDebug()) {
      debugError("api", `Ошибка ${response.status} ${path}`, data);
    }
    throw new ApiError(
      data.message ?? `Ошибка запроса (${response.status})`,
      response.status,
      data.errors,
    );
  }

  return data as T;
}

export const api = {
  auth: {
    login: (email: string, password: string) =>
      request<AuthResponse>("/auth/login", {
        method: "POST",
        body: { email, password },
      }),
    logout: () => request<{ message: string }>("/auth/logout", { method: "POST" }),
    me: () => request<MeResponse>("/auth/me"),
  },

  contact: {
    submit: (data: ContactRequest) =>
      request<{ message: string; contact: ContactRequest }>("/contact", {
        method: "POST",
        body: data,
      }),
  },

  admin: {
    stats: () => request<AdminStats>("/admin/stats"),
    companies: {
      list: (params?: { search?: string; page?: number }) => {
        const q = new URLSearchParams();
        if (params?.search) q.set("search", params.search);
        if (params?.page) q.set("page", String(params.page));
        const qs = q.toString();
        return request<PaginatedResponse<Company>>(
          `/admin/companies${qs ? `?${qs}` : ""}`,
        );
      },
      get: (id: number) => request<Company>(`/admin/companies/${id}`),
      create: (data: Partial<Company>) =>
        request<Company>("/admin/companies", { method: "POST", body: data }),
      update: (id: number, data: Partial<Company>) =>
        request<Company>(`/admin/companies/${id}`, {
          method: "PUT",
          body: data,
        }),
      delete: (id: number) =>
        request<{ message: string }>(`/admin/companies/${id}`, {
          method: "DELETE",
        }),
    },
    documents: {
      list: (params?: { status?: DocumentStatus; company_id?: number; page?: number }) => {
        const q = new URLSearchParams();
        if (params?.status) q.set("status", params.status);
        if (params?.company_id) q.set("company_id", String(params.company_id));
        if (params?.page) q.set("page", String(params.page));
        const qs = q.toString();
        return request<PaginatedResponse<Document>>(
          `/admin/documents${qs ? `?${qs}` : ""}`,
        );
      },
      get: (id: number) => request<Document>(`/admin/documents/${id}`),
      delete: (id: number) =>
        request<{ message: string }>(`/admin/documents/${id}`, {
          method: "DELETE",
        }),
    },
  },

  company: {
    settings: {
      get: () => request<{ company: Company }>("/company/settings"),
      update: (data: FormData | Partial<Company>) => {
        if (data instanceof FormData) {
          return request<{ company: Company }>("/company/settings", {
            method: "POST",
            formData: data,
          });
        }
        return request<{ company: Company }>("/company/settings", {
          method: "PUT",
          body: data,
        });
      },
    },
  },

  documents: {
    list: (params?: { status?: DocumentStatus; page?: number }) => {
      const q = new URLSearchParams();
      if (params?.status) q.set("status", params.status);
      if (params?.page) q.set("page", String(params.page));
      const qs = q.toString();
      return request<PaginatedResponse<Document>>(
        `/documents${qs ? `?${qs}` : ""}`,
      );
    },
    get: (id: number, params?: { meta?: boolean }) => {
      const q = params?.meta ? "?meta=1" : "";
      return request<Document>(`/documents/${id}${q}`);
    },
    update: (id: number, data: { title: string }) =>
      request<Document>(`/documents/${id}`, { method: "PATCH", body: data }),
    versions: (id: number) =>
      request<{ versions: import("../types/models").DocumentVersion[]; active_version_id: number | null }>(
        `/documents/${id}/versions`,
      ),
    activateVersion: (documentId: number, versionId: number) =>
      request<{ message: string; active_version_id: number; document: Document }>(
        `/documents/${documentId}/versions/${versionId}/activate`,
        { method: "POST" },
      ),
    upload: (file: File, title?: string) => {
      const formData = new FormData();
      formData.append("file", file);
      if (title) formData.append("title", title);
      return request<Document>("/documents", { method: "POST", formData });
    },
    delete: (id: number) =>
      request<{ message: string }>(`/documents/${id}`, { method: "DELETE" }),
    process: (id: number) =>
      request<{ message: string; document: Document }>(
        `/documents/${id}/process`,
        { method: "POST" },
      ),
    status: (id: number) =>
      request<{
        id: number;
        status: DocumentStatus;
        error_message: string | null;
        active_version_id: number | null;
      }>(`/documents/${id}/status`),
    layout: (id: number) =>
      request<DocumentLayout>(`/documents/${id}/layout`),
    updateLayout: (
      id: number,
      data: {
        header_html?: string | null;
        footer_html?: string | null;
        first_page_header_html?: string | null;
        first_page_footer_html?: string | null;
        different_first_page?: boolean;
        page_settings?: DocumentPageSettings;
      },
    ) =>
      request<DocumentLayout>(`/documents/${id}/layout`, {
        method: "PATCH",
        body: data,
      }),
    blocks: {
      list: (
        documentId: number,
        params?: { source_page?: number; outline?: boolean; page?: number; per_page?: number },
      ) => {
        const search = new URLSearchParams();
        if (params?.source_page) {
          search.set("source_page", String(params.source_page));
        }
        if (params?.outline) {
          search.set("outline", "1");
        }
        if (params?.page) {
          search.set("page", String(params.page));
        }
        if (params?.per_page) {
          search.set("per_page", String(params.per_page));
        }
        const query = search.toString();
        return request<{
          blocks: DocumentBlock[];
          meta?: { page: number; per_page: number; total: number; last_page: number };
        }>(
          `/documents/${documentId}/blocks${query ? `?${query}` : ""}`,
        );
      },
      get: (documentId: number, blockId: number) =>
        request<{ block: DocumentBlock }>(
          `/documents/${documentId}/blocks/${blockId}`,
        ),
      update: (
        documentId: number,
        blockId: number,
        data: Partial<DocumentBlock>,
      ) =>
        request<{ block: DocumentBlock }>(
          `/documents/${documentId}/blocks/${blockId}`,
          { method: "PUT", body: data },
        ),
      restore: (documentId: number, blockId: number) =>
        request<{ block: DocumentBlock }>(
          `/documents/${documentId}/blocks/${blockId}/restore`,
          { method: "POST" },
        ),
      convertToButton: (
        documentId: number,
        blockId: number,
        label?: string,
      ) =>
        request<{ block: DocumentBlock }>(
          `/documents/${documentId}/blocks/${blockId}/convert-to-button`,
          { method: "POST", body: label ? { label } : {} },
        ),
      attachAsset: (documentId: number, blockId: number, assetId: number) =>
        request<{ block: DocumentBlock }>(
          `/documents/${documentId}/blocks/${blockId}/attach-asset`,
          { method: "POST", body: { asset_id: assetId } },
        ),
      reorder: (
        documentId: number,
        data: {
          block_id: number;
          after_block_id?: number | null;
          source_page?: number;
          restore_display_content?: string;
        },
      ) =>
        request<{ blocks: DocumentBlock[] }>(
          `/documents/${documentId}/blocks/reorder`,
          { method: "POST", body: data },
        ),
      embed: (
        documentId: number,
        sourceBlockId: number,
        targetBlockId: number,
        data: {
          target_display_content: string;
          source_display_content?: string;
        },
      ) =>
        request<{ source: DocumentBlock; target: DocumentBlock }>(
          `/documents/${documentId}/blocks/${sourceBlockId}/embed-into/${targetBlockId}`,
          { method: "POST", body: data },
        ),
      batchUpdate: (
        documentId: number,
        blocks: Array<{ id: number; display_content?: string; settings_json?: Record<string, unknown> }>,
      ) =>
        request<{ blocks: DocumentBlock[] }>(
          `/documents/${documentId}/blocks/batch`,
          { method: "PATCH", body: { blocks } },
        ),
    },
    comments: {
      list: (documentId: number) =>
        request<{ comments: import("@/lib/documentComments").DocumentComment[] }>(
          `/documents/${documentId}/comments`,
        ),
      create: (
        documentId: number,
        data: {
          block_id?: number;
          parent_id?: number;
          comment_type?: "comment" | "suggestion";
          anchor_id?: string;
          source_page?: number;
          quoted_text?: string;
          body: string;
          suggested_html?: string;
        },
      ) =>
        request<{ comment: import("@/lib/documentComments").DocumentComment }>(
          `/documents/${documentId}/comments`,
          { method: "POST", body: data },
        ),
      update: (
        documentId: number,
        commentId: number,
        data: { body?: string; resolved?: boolean; suggested_html?: string },
      ) =>
        request<{ comment: import("@/lib/documentComments").DocumentComment }>(
          `/documents/${documentId}/comments/${commentId}`,
          { method: "PATCH", body: data },
        ),
      delete: (documentId: number, commentId: number) =>
        request<{ message: string }>(
          `/documents/${documentId}/comments/${commentId}`,
          { method: "DELETE" },
        ),
    },
    assets: {
      list: (documentId: number) =>
        request<{ assets: DocumentAsset[] }>(
          `/documents/${documentId}/assets`,
        ),
      get: (documentId: number, assetId: number) =>
        request<{ asset: DocumentAsset }>(
          `/documents/${documentId}/assets/${assetId}`,
        ),
      fileUrl: (documentId: number, assetId: number) =>
        `${API_URL}/documents/${documentId}/assets/${assetId}/file`,
      fetchFileBlob: async (documentId: number, assetId: number): Promise<string> => {
        const token = getToken();
        const response = await fetch(
          `${API_URL}/documents/${documentId}/assets/${assetId}/file`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: "include",
          },
        );
        if (!response.ok) {
          throw new ApiError("Не удалось загрузить файл", response.status);
        }
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      },
      fetchTable: (documentId: number, assetId: number) =>
        request<{ table: { id: number; html: string; title: string } }>(
          `/documents/${documentId}/assets/${assetId}/table`,
        ),
      update: (
        documentId: number,
        assetId: number,
        data: FormData | { metadata_json?: Record<string, unknown> },
      ) => {
        if (data instanceof FormData) {
          return request<{ asset: DocumentAsset }>(
            `/documents/${documentId}/assets/${assetId}`,
            { method: "POST", formData: data },
          );
        }
        return request<{ asset: DocumentAsset }>(
          `/documents/${documentId}/assets/${assetId}`,
          { method: "PUT", body: data },
        );
      },
    },
    exports: {
      list: (documentId: number) =>
        request<{ exports: DocumentExport[] }>(
          `/documents/${documentId}/exports`,
        ),
      createHtml: (documentId: number) =>
        request<{ export: DocumentExport }>(
          `/documents/${documentId}/exports/html`,
          { method: "POST" },
        ),
      createPdf: (documentId: number) =>
        request<{ export: DocumentExport }>(
          `/documents/${documentId}/exports/pdf`,
          { method: "POST" },
        ),
      createDocx: (documentId: number) =>
        request<{ export: DocumentExport }>(
          `/documents/${documentId}/exports/docx`,
          { method: "POST" },
        ),
      get: (documentId: number, exportId: number) =>
        request<{ export: DocumentExport }>(
          `/documents/${documentId}/exports/${exportId}`,
        ),
      downloadUrl: (documentId: number, exportId: number) =>
        `${API_URL}/documents/${documentId}/exports/${exportId}?download=1`,
    },
  },
};
